import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, ExternalLink, Printer, Building2, Loader2 } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Phase 25.3 — shows every Statement generated from a Carrier Import. */
export default function GeneratedStatementsCard({ importId }) {
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!importId) return;
    setLoading(true);
    accountingAPI.statementsForImport(importId)
      .then((r) => setStatements(r.data.items || []))
      .catch(() => setStatements([]))
      .finally(() => setLoading(false));
  }, [importId]);

  const downloadPdf = async (s) => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.statementPdfUrl(s.statement_id)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `${s.statement_number}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('PDF download failed'); }
  };

  const printPdf = async (s) => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.statementPdfUrl(s.statement_id)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const b = await r.blob();
      const w = window.open(URL.createObjectURL(b), '_blank');
      if (w) w.addEventListener('load', () => w.print());
    } catch { toast.error('Print failed'); }
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;
  }
  if (statements.length === 0) return null;

  const totalNet = statements.reduce((sum, s) => sum + (Number(s.net_total) || 0), 0);

  return (
    <Card data-testid="generated-statements-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText size={15} /> Generated Statements
          <Badge className="ml-1 bg-emerald-100 text-emerald-800 font-mono">{statements.length}</Badge>
        </CardTitle>
        <div className="text-xs text-slate-500">
          Total net · <span className="font-mono font-semibold text-slate-800">{money(totalNet)}</span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-xs" data-testid="statements-table">
          <thead>
            <tr className="border-b bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <th className="py-2 px-3">Statement #</th>
              <th className="py-2 px-3">Recipient</th>
              <th className="py-2 px-3">Lines</th>
              <th className="py-2 px-3 text-right">Positive</th>
              <th className="py-2 px-3 text-right">Chargebacks</th>
              <th className="py-2 px-3 text-right">Net Total</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.statement_id} className="border-b border-slate-50 hover:bg-slate-50/60"
                  data-testid={`statement-row-${s.statement_id}`}>
                <td className="py-2 px-3 font-mono font-semibold text-slate-800">{s.statement_number}</td>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-1.5">
                    <Building2 size={11} className="text-slate-400" />
                    <div>
                      <div className="font-medium">{s.recipient_name}</div>
                      <div className="text-[10px] text-slate-500 capitalize">{s.recipient_type}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3">{s.total_lines}</td>
                <td className="py-2 px-3 text-right font-mono text-emerald-700">{money(s.positive_amount)}</td>
                <td className="py-2 px-3 text-right font-mono text-red-700">{money(s.negative_amount)}</td>
                <td className={`py-2 px-3 text-right font-mono font-semibold ${
                  s.net_total < 0 ? 'text-red-700' : 'text-slate-800'
                }`}>{money(s.net_total)}</td>
                <td className="py-2 px-3">
                  <Badge className={s.status === 'negative_balance'
                      ? 'bg-red-100 text-red-800 text-[10px] py-0 px-1.5'
                      : 'bg-emerald-100 text-emerald-800 text-[10px] py-0 px-1.5'}>
                    {s.status.replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="py-2 px-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" title="Open" onClick={() => navigate(`/accounting/statements/${s.statement_id}`)}
                            data-testid={`open-statement-${s.statement_id}`}>
                      <ExternalLink size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" title="Download PDF" onClick={() => downloadPdf(s)}
                            data-testid={`download-statement-${s.statement_id}`}>
                      <Download size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" title="Print" onClick={() => printPdf(s)}
                            data-testid={`print-statement-${s.statement_id}`}>
                      <Printer size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
